/**
 * Supplier Selection Card Component
 * Handles supplier search, selection, and user management for chat creation
 */

import type {
  UserSelectOption,
  CreateChatFormData,
  SupplierUserSelectOption,
} from '@choco-admin/types';
import {
  Button,
  Card,
  CardBody,
  Autocomplete,
  AutocompleteItem,
} from '@heroui/react';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FiUser, FiUsers, FiX } from 'react-icons/fi';

import { Pill } from '../../../../../../components/Pill';
import { useSearchSuppliersForChat } from '../../../../hooks/useSearchSuppliersForChat';
import { useSearchUsersForChat } from '../../../../hooks/useSearchUsersForChat';

interface SupplierSelectionCardProps {
  onSupplierSelect: (supplier: UserSelectOption) => void;
  onSupplierUserToggle: (user: SupplierUserSelectOption) => void;
  onRemoveSupplierUser: (userId: string) => void;
}

export const SupplierSelectionCard: React.FC<SupplierSelectionCardProps> = ({
  onSupplierSelect,
  onSupplierUserToggle,
  onRemoveSupplierUser,
}) => {
  const { control, watch, setValue } = useFormContext<CreateChatFormData>();
  const formData = watch();

  // Data hooks
  const {
    suppliers,
    isLoading: suppliersLoading,
    searchSuppliers,
  } = useSearchSuppliersForChat();

  const {
    supplierUsers,
    isLoading: usersLoading,
    searchUsers,
  } = useSearchUsersForChat();

  return (
    <Card>
      <CardBody className="space-y-4">
        <div className="flex items-center gap-2">
          <FiUser className="text-secondary" />
          <h4 className="font-medium">Supplier *</h4>
        </div>

        <Controller
          name="supplier"
          control={control}
          render={({ field, fieldState }) => (
            <Autocomplete
              placeholder="Type to search suppliers..."
              aria-label="Search and select supplier"
              isLoading={suppliersLoading}
              errorMessage={fieldState.error?.message}
              isInvalid={!!fieldState.error}
              selectedKey={field.value?.value || null}
              items={suppliers}
              onInputChange={(value) => {
                if (value.length > 2) {
                  searchSuppliers(value);
                } else if (value.length === 0) {
                  // Clear results when input is empty
                  // This helps with the re-rendering issue
                }
              }}
              onSelectionChange={(key) => {
                if (key) {
                  const selectedSupplier = suppliers.find(
                    (supplier: UserSelectOption) => supplier.value === key
                  );
                  if (selectedSupplier) {
                    onSupplierSelect(selectedSupplier);
                  }
                } else {
                  setValue('supplier', null);
                  setValue('supplierUsers', []);
                }
              }}
              classNames={{
                base: 'max-w-full',
              }}
            >
              {(supplier: UserSelectOption) => (
                <AutocompleteItem
                  key={supplier.value}
                  textValue={supplier.label}
                  className="py-3 border-b border-divider last:border-b-0"
                >
                  <div className="flex flex-col py-1">
                    <span className="text-small font-medium text-foreground">
                      {supplier.label}
                    </span>
                    {supplier.metaData?.phone && (
                      <span className="text-tiny text-foreground-600 mt-1">
                        {supplier.metaData.phone}
                      </span>
                    )}
                  </div>
                </AutocompleteItem>
              )}
            </Autocomplete>
          )}
        />

        {/* Selected Supplier */}
        {formData.supplier && (
          <Card className="bg-secondary-50 border-secondary-200">
            <CardBody className="p-3">
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-secondary-800 break-words">
                    {formData.supplier.label}
                  </div>
                  {formData.supplier.metaData?.phone && (
                    <div className="text-xs text-secondary-600 mt-1 break-words">
                      {formData.supplier.metaData.phone}
                    </div>
                  )}
                </div>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  className="flex-shrink-0"
                  onPress={() => {
                    setValue('supplier', null);
                    setValue('supplierUsers', []);
                  }}
                >
                  <FiX />
                </Button>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Supplier Users */}
        {formData.supplier && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <FiUsers className="text-default-500" />
              <span className="text-sm font-medium">Supplier Users</span>
              <span className="text-xs text-foreground-600">(optional)</span>
            </div>

            <Autocomplete
              placeholder="Search supplier users..."
              aria-label="Search and select supplier users"
              isLoading={usersLoading}
              selectedKey={null}
              items={supplierUsers}
              onInputChange={(value) => {
                if (value.length > 2) {
                  searchUsers(value, 'supplier');
                }
              }}
              onSelectionChange={(key) => {
                if (key) {
                  const selectedUser = supplierUsers.find(
                    (user: SupplierUserSelectOption) => user.value === key
                  );
                  if (selectedUser) {
                    onSupplierUserToggle(selectedUser);
                  }
                }
              }}
              classNames={{
                base: 'max-w-full',
              }}
            >
              {(user: SupplierUserSelectOption) => (
                <AutocompleteItem
                  key={user.value}
                  textValue={user.label}
                  className="py-3 border-b border-divider last:border-b-0"
                >
                  <div className="flex flex-col py-1">
                    <span className="text-small font-medium text-foreground">
                      {user.label}
                    </span>
                    {user.metaData?.email && (
                      <span className="text-tiny text-foreground-600 mt-1">
                        {user.metaData.email}
                      </span>
                    )}
                  </div>
                </AutocompleteItem>
              )}
            </Autocomplete>

            {/* Selected Supplier Users */}
            {formData.supplierUsers && formData.supplierUsers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.supplierUsers.map((user) => (
                  <Pill
                    key={user.value}
                    variant="secondary"
                    size="sm"
                    className="max-w-full"
                    onClick={() => onRemoveSupplierUser(user.value)}
                  >
                    <div className="flex items-center gap-1">
                      <span className="break-words truncate">{user.label}</span>
                      <FiX className="text-xs cursor-pointer" />
                    </div>
                  </Pill>
                ))}
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
};
